import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../firebase";
import logoImg from "../../assets/logo.png";
import {
  collection,
  getDocs,
  setDoc,
  doc,
  updateDoc,
  serverTimestamp,
  query,
  orderBy,
} from "firebase/firestore";
import { getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";



// PDF export libs (install if you haven’t):
// npm i jspdf html2canvas
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

/**
 * --- PAYE CONFIG (Weekly payroll; update each tax year) ---
 * You can later move this into Firestore at: settings/payroll
 * and load it at runtime.
 */
const DEFAULT_PAYE = {
  taxCode: "1257L", // personal allowance 12,570
  frequency: "weekly",
  personalAllowanceAnnual: 12570,
  // Income tax bands (England/Wales)
  bands: [
    { upToAnnual: 37700, rate: 0.20 }, // basic
    { upToAnnual: 125140, rate: 0.40 }, // higher
    { upToAnnual: Infinity, rate: 0.45 }, // additional
  ],
  // NI (Class 1 employee). Thresholds expressed weekly
  ni: {
    primaryThresholdWeekly: 242, // start paying employee NI above this (approx; update per year)
    upperEarningsLimitWeekly: 967,
    mainRate: 0.12, // employee
    upperRate: 0.02, // employee
  },
  // Optional extras
  studentLoan: { enabled: false, plan: "Plan 2", thresholdWeekly: 524, rate: 0.09 },
  pension: { enabled: false, employeeRate: 0.05 }, // 5% employee (example)
};

// ---- Helpers ----
const fmtGBP = (n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(
    n || 0
  );

const toWeeklyAllowance = (annual) => annual / 52;

// Very simple PAYE/NI calc for a *weekly* period
function calcWeeklyPAYE(gross, taxCode, niCategory, studentLoanPlan) {
  gross = Number(gross || 0);

  // ----------------------------------
  // 1) NORMAL TAX BANDS (for codes like 1257L, 0T, K***)
  // ----------------------------------
  let personalAllowanceWeekly = taxCode === "0T" ? 0 : 12570 / 52;

  // K codes → negative allowance (e.g., K278 → add 278×10 to tax)
  if (taxCode?.startsWith("K")) {
    const kVal = parseInt(taxCode.substring(1)) || 0;
    personalAllowanceWeekly = -(kVal * 10) / 52;
  }

  // taxable income
  let taxable = gross - personalAllowanceWeekly;

  // ----------------------------------
  // 2) SPECIAL TAX CODES
  // ----------------------------------
  if (taxCode === "BR" || taxCode === "BR wk1mth1") {
    return {
      gross,
      tax: Number((gross * 0.20).toFixed(2)),
      ni: calcNI(gross, niCategory),
      studentLoan: calcStudentLoan(gross, studentLoanPlan),
      pension: 0,
      net: Number((gross - (gross * 0.20) - calcNI(gross, niCategory) - calcStudentLoan(gross, studentLoanPlan)).toFixed(2)),
      personalAllowanceWeekly: 0,
    };
  }

  if (taxCode === "D0") {
    return {
      gross,
      tax: Number((gross * 0.40).toFixed(2)),
      ni: calcNI(gross, niCategory),
      studentLoan: calcStudentLoan(gross, studentLoanPlan),
      pension: 0,
      net: Number((gross - (gross * 0.40) - calcNI(gross, niCategory) - calcStudentLoan(gross, studentLoanPlan)).toFixed(2)),
      personalAllowanceWeekly: 0,
    };
  }

  if (taxCode === "D1") {
    return {
      gross,
      tax: Number((gross * 0.45).toFixed(2)),
      ni: calcNI(gross, niCategory),
      studentLoan: calcStudentLoan(gross, studentLoanPlan),
      pension: 0,
      net: Number((gross - (gross * 0.45) - calcNI(gross, niCategory) - calcStudentLoan(gross, studentLoanPlan)).toFixed(2)),
      personalAllowanceWeekly: 0,
    };
  }

  // ----------------------------------
  // 3) NORMAL TAX BANDS calculation
  // ----------------------------------
  const bands = [
    { limit: 37700 / 52, rate: 0.20 },
    { limit: 125140 / 52, rate: 0.40 },
    { limit: Infinity, rate: 0.45 },
  ];

  let remaining = Math.max(taxable, 0);
  let tax = 0;
  let lower = 0;

  for (const b of bands) {
    const slice = Math.min(remaining, b.limit - lower);
    if (slice > 0) tax += slice * b.rate;
    remaining -= slice;
    lower = b.limit;
    if (remaining <= 0) break;
  }

  const ni = calcNI(gross, niCategory);
  const sl = calcStudentLoan(gross, studentLoanPlan);

  const net = gross - tax - ni - sl;

  return {
    gross,
    tax: Number(tax.toFixed(2)),
    ni,
    studentLoan: sl,
    pension: 0,
    net: Number(net.toFixed(2)),
    personalAllowanceWeekly: Number(personalAllowanceWeekly.toFixed(2)),
  };
}






function calcNI(gross, category = "A") {
  gross = Number(gross || 0);

  const PT = 242;
  const UEL = 967;

  let main = 0.12;
  let upper = 0.02;

  // Category-specific rates
  if (category === "C") {
    return 0; // Over State Pension age
  }
  if (category === "H") {
    main = 0.0; // Apprentice under 25
  }
  if (category === "M") {
    main = 0.0; // Under 21 NI free
  }
  if (category === "Z") {
    main = 0.0; // Under 21 NI free at secondary
  }

  let ni = 0;
  if (gross > PT) {
    ni += Math.min(gross, UEL) - PT > 0 ? (Math.min(gross, UEL) - PT) * main : 0;
    ni += gross > UEL ? (gross - UEL) * upper : 0;
  }

  return Number(ni.toFixed(2));
}





function calcStudentLoan(gross, plan = "None") {
  gross = Number(gross || 0);

  const plans = {
    Plan1: { threshold: 388, rate: 0.09 },
    Plan2: { threshold: 524, rate: 0.09 },
    Plan4: { threshold: 487, rate: 0.09 },
    PostGrad: { threshold: 403, rate: 0.06 },
  };

  if (!plans[plan]) return 0;

  const { threshold, rate } = plans[plan];

  if (gross <= threshold) return 0;

  return Number(((gross - threshold) * rate).toFixed(2));
}


export default function TimesheetManager() {
  const [loading, setLoading] = useState(false);
  const [timesheets, setTimesheets] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState("latest");
  const [payslips, setPayslips] = useState([]);
  const [payeCfg, setPayeCfg] = useState(DEFAULT_PAYE);
  const navigate = useNavigate();

  // ──────────────────────────────────────────────
  // Generate Timesheets from latest rota (your existing handler kept)
  // ──────────────────────────────────────────────
  const generateTimesheets = async () => {
  setLoading(true);
  try {
    const rotaSnap = await getDocs(collection(db, "rotas"));
    if (rotaSnap.empty) {
      alert("⚠️ No rotas found. Please generate a rota first.");
      setLoading(false);
      return;
    }

    // Get the latest rota
    const rotas = rotaSnap.docs.map((d) => d.data());
    const latestRota = rotas[rotas.length - 1];
    const assignments = latestRota.assignments || [];

    const staffGrouped = {};
    for (const a of assignments) {
      // ✅ Fetch staff and client rates
      let rate = 15;
      try {
        const staffDoc = await getDoc(doc(db, "staff", a.staffId));
        const clientDoc = await getDoc(doc(db, "clients", a.clientId));

        if (staffDoc.exists() && staffDoc.data().rate)
          rate = staffDoc.data().rate;
        else if (clientDoc.exists() && clientDoc.data().rate)
          rate = clientDoc.data().rate;
      } catch (err) {
        console.warn("Rate lookup failed:", err);
      }

      if (!staffGrouped[a.staffId]) {
        staffGrouped[a.staffId] = {
          staffId: a.staffId,
          staffName: a.staffName,
          weekStart: latestRota.weekStart,
          weekEnd: latestRota.weekEnd,
          entries: [],
          totalHours: 0,
          totalPay: 0,
          status: "Pending",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
      }

      const hours = a.totalHours || 0;

      staffGrouped[a.staffId].entries.push({
        date: a.date,
        clientName: a.clientName,
        hours,
        rate,
        approved: false,
      });

      staffGrouped[a.staffId].totalHours += hours;
      staffGrouped[a.staffId].totalPay += rate * hours;
    }

    for (const [staffId, data] of Object.entries(staffGrouped)) {
      await setDoc(
        doc(db, "timesheets", `${staffId}_week_${latestRota.weekStart}`),
        data,
        { merge: true }
      );
    }

    alert("✅ Timesheets generated successfully!");
    await loadTimesheets();
  } catch (error) {
    console.error("Error generating timesheets:", error);
    alert("❌ Failed to generate timesheets. Check console for details.");
  } finally {
    setLoading(false);
  }
};


  // ──────────────────────────────────────────────
  // Load timesheets (latest week first)
  // ──────────────────────────────────────────────
  const loadTimesheets = async () => {
  setLoading(true);
  try {
    const snap = await getDocs(collection(db, "timesheets"));
    const list = snap.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        ...data,
        weekStart: data.weekStart?.toDate
          ? data.weekStart.toDate().toISOString().slice(0, 10)
          : data.weekStart,
        weekEnd: data.weekEnd?.toDate
          ? data.weekEnd.toDate().toISOString().slice(0, 10)
          : data.weekEnd,
        entries: data.entries || [],
      };
    });

    list.sort((a, b) => String(b.weekStart).localeCompare(String(a.weekStart)));
    setTimesheets(list);
    if (list.length && selectedWeek === "latest") {
      setSelectedWeek(list[0].weekStart);
    }
  } catch (e) {
    console.error("Failed to load timesheets:", e);
    alert("Failed to load timesheets.");
  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    loadTimesheets();
  }, []);

  const weeks = useMemo(() => {
    const set = new Set(timesheets.map((t) => t.weekStart));
    return Array.from(set).sort((a, b) => String(b).localeCompare(String(a)));
  }, [timesheets]);

  const visibleTimesheets = useMemo(() => {
    if (selectedWeek === "latest" && weeks.length) {
      return timesheets.filter((t) => t.weekStart === weeks[0]);
    }
    return timesheets.filter((t) => t.weekStart === selectedWeek);
  }, [timesheets, selectedWeek, weeks]);

  // ──────────────────────────────────────────────
  // Approvals
  // ──────────────────────────────────────────────
  const toggleEntryApproval = async (tsId, idx) => {
    const ts = timesheets.find((t) => t.id === tsId);
    if (!ts) return;

    const entries = [...ts.entries];
    entries[idx] = { ...entries[idx], approved: !entries[idx].approved };

    // recompute totals for approved only
    const approvedGross = entries
      .filter((e) => e.approved)
      .reduce((sum, e) => sum + e.hours * e.rate, 0);

    const status = entries.some((e) => e.approved) ? "Submitted" : "Pending";

    await updateDoc(doc(db, "timesheets", tsId), {
      entries,
      totalHours: entries.reduce((sum, e) => sum + e.hours, 0),
      totalPay: entries.reduce((sum, e) => sum + e.hours * e.rate, 0),
      approvedGross,
      status,
      updatedAt: serverTimestamp(),
    });

    await loadTimesheets();
  };

  const approveAllEntries = async (tsId, approve = true) => {
    const ts = timesheets.find((t) => t.id === tsId);
    if (!ts) return;

    const entries = ts.entries.map((e) => ({ ...e, approved: approve }));
    const approvedGross = entries
      .filter((e) => e.approved)
      .reduce((sum, e) => sum + e.hours * e.rate, 0);

    const status = approve ? "Submitted" : "Pending";

    await updateDoc(doc(db, "timesheets", tsId), {
      entries,
      approvedGross,
      status,
      updatedAt: serverTimestamp(),
    });

    await loadTimesheets();
  };

  // ──────────────────────────────────────────────
  // Generate Payslips from approved timesheets
  // ──────────────────────────────────────────────
  const generatePayslipsFromApproved = async () => {
    setLoading(true);
    try {
      const target = visibleTimesheets.filter(
        (t) => (t.approvedGross || 0) > 0
      );

      if (!target.length) {
        alert("No approved entries found for this week.");
        setLoading(false);
        return;
      }

      const batchWrites = [];
      for (const ts of target) {
        const gross = ts.entries
          .filter((e) => e.approved)
          .reduce((sum, e) => sum + e.hours * e.rate, 0);

        const paye = calcWeeklyPAYE(
			  gross,
			  ts.taxCode,
			  ts.niCategory,
			  ts.studentLoan
			);


        const payslip = {
          staffId: ts.staffId,
          staffName: ts.staffName,
          weekStart: ts.weekStart,
          weekEnd: ts.weekEnd,
          period: "Weekly",
          grossPay: Number(gross.toFixed(2)),
          incomeTax: paye.tax,
          employeeNI: paye.ni,
          studentLoan: paye.studentLoan,
          pensionEmployee: paye.pension,
          netPay: paye.net,
          personalAllowanceWeekly: paye.personalAllowanceWeekly,
          taxCode: ts.taxCode || payeCfg.taxCode,
		  niCategory: ts.niCategory || "A",
		  studentLoanPlan: ts.studentLoan || "None",
          createdAt: serverTimestamp(),
          fromTimesheetId: ts.id,
          // Optional breakdown:
          entries: ts.entries.filter((e) => e.approved),
        };

        const slipId = `${ts.staffId}_${ts.weekStart}`;
        batchWrites.push(
          setDoc(doc(db, "payslips", slipId), payslip, { merge: true })
        );

        // Mark timesheet as Approved (final)
        batchWrites.push(
          updateDoc(doc(db, "timesheets", ts.id), {
            status: "Approved",
            updatedAt: serverTimestamp(),
          })
        );
      }

      await Promise.all(batchWrites);
      alert("✅ Payslips generated!");
      await loadPayslipsForWeek(selectedWeek === "latest" ? weeks[0] : selectedWeek);
      await loadTimesheets();
    } catch (e) {
      console.error(e);
      alert("❌ Failed to generate payslips.");
    } finally {
      setLoading(false);
    }
  };

  // ──────────────────────────────────────────────
  // Load payslips for week
  // ──────────────────────────────────────────────
  const loadPayslipsForWeek = async (week) => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "payslips"));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const filtered = list.filter((p) => p.weekStart === week);
      setPayslips(filtered);
    } catch (e) {
      console.error(e);
      alert("Failed to load payslips.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const wk = selectedWeek === "latest" ? weeks[0] : selectedWeek;
    if (wk) loadPayslipsForWeek(wk);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWeek, weeks.length]);

  // ──────────────────────────────────────────────
  // Export helpers (PDF)
  // ──────────────────────────────────────────────
  const exportElementToPDF = async (elementId, filename) => {
  const el = document.getElementById(elementId);
  if (!el) {
    alert("⚠️ Element not found for export.");
    return;
  }

  // Temporarily unhide element if it's hidden
  const prevDisplay = el.style.display;
  el.style.display = "block";

  try {
    // Capture HTML element
    const canvas = await html2canvas(el, {
      scale: 2,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
      useCORS: true,
    });
    const imgData = canvas.toDataURL("image/png");

    // Create PDF
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Header
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    pdf.text("CCR — Convalesce Care & Recruitment", 15, 20);
    pdf.setFontSize(10);
    pdf.text("Professional and Expert Touch", 15, 28);
    pdf.setDrawColor(200);
    pdf.line(15, 35, pageWidth - 15, 35);

    // Add content image
    const imgWidth = pageWidth - 20;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    pdf.addImage(imgData, "PNG", 10, 40, imgWidth, Math.min(imgHeight, pageHeight - 50));

    // Footer
    const pageCount = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      pdf.setFontSize(9);
      pdf.setTextColor(130);
      pdf.text(
        `Page ${i} of ${pageCount}`,
        pageWidth - 50,
        pageHeight - 10
      );
    }

    // Save file
    pdf.save(filename);
  } catch (err) {
    console.error("PDF generation failed:", err);
    alert("❌ PDF generation failed. Check console for details.");
  } finally {
    // Restore element display
    el.style.display = prevDisplay;
  }
};



	const viewPayslips = () => {
		alert("📄 Viewing all payslips...");
	  };
	  
  const exportTimesheetsPDF = async () => {
  const elementId = "timesheet-list";
  const el = document.getElementById(elementId);
  if (!el) {
    alert("⚠️ No timesheet content to export.");
    return;
  }

  const pdf = new jsPDF("p", "pt", "a4");
  const pageWidth = pdf.internal.pageSize.getWidth();

  // --- Header Section ---
  const logo = new Image();
  logo.src = logoImg; // ✅ ensure you've imported logoImg at top of file
  const headerY = 30;

  pdf.addImage(logo, "PNG", 40, headerY, 50, 50);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(16);
  pdf.text("Convalesce Care & Recruitment", 100, headerY + 20);
  pdf.setFontSize(10);
  pdf.text("Professional & Expert Touch", 100, headerY + 35);

  pdf.setDrawColor(200);
  pdf.line(40, headerY + 60, pageWidth - 40, headerY + 60);

  const weekDisplay = selectedWeek === "latest" ? weeks[0] : selectedWeek;
  pdf.setFontSize(12);
  pdf.text(`Weekly Timesheets — Week of ${weekDisplay}`, 40, headerY + 80);

  // --- Capture content area ---
  const canvas = await html2canvas(el, {
    scale: 2,
    backgroundColor: "#ffffff",
    scrollY: -window.scrollY,
  });
  const imgData = canvas.toDataURL("image/png");

  const imgWidth = pageWidth - 80;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;
  let heightLeft = imgHeight;
  let position = headerY + 100;

  pdf.addImage(imgData, "PNG", 40, position, imgWidth, imgHeight);
  heightLeft -= pdf.internal.pageSize.getHeight() - position;

  while (heightLeft > 0) {
    pdf.addPage();
    position = 40;
    pdf.addImage(imgData, "PNG", 40, position - heightLeft, imgWidth, imgHeight);
    heightLeft -= pdf.internal.pageSize.getHeight() - 40;
  }

  // --- Footer ---
  const pageCount = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(9);
    pdf.setTextColor(130);
    pdf.text(
      `Page ${i} of ${pageCount}`,
      pageWidth - 80,
      pdf.internal.pageSize.getHeight() - 30
    );
  }

  pdf.save(`Convalesce_Timesheets_${weekDisplay}.pdf`);
};


  const exportPayslipsPDF = async () => {
  const elementId = "payslip-list";
  const el = document.getElementById(elementId);
  if (!el) {
    alert("⚠️ No payslips to export.");
    return;
  }

  const pdf = new jsPDF("p", "pt", "a4");
  const pageWidth = pdf.internal.pageSize.getWidth();

  // --- Header ---
  const logo = new Image();
  logo.src = logoImg;
  const headerY = 30;

  pdf.addImage(logo, "PNG", 40, headerY, 50, 50);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(16);
  pdf.text("Convalesce Care & Recruitment", 100, headerY + 20);
  pdf.setFontSize(10);
  pdf.text("Professional & Expert Touch", 100, headerY + 35);

  pdf.setDrawColor(200);
  pdf.line(40, headerY + 60, pageWidth - 40, headerY + 60);

  const weekDisplay = selectedWeek === "latest" ? weeks[0] : selectedWeek;
  pdf.setFontSize(12);
  pdf.text(`Payslips — Week of ${weekDisplay}`, 40, headerY + 80);

  // --- Capture payslip list ---
  const canvas = await html2canvas(el, {
    scale: 2,
    backgroundColor: "#ffffff",
    scrollY: -window.scrollY,
  });
  const imgData = canvas.toDataURL("image/png");

  const imgWidth = pageWidth - 80;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;
  let heightLeft = imgHeight;
  let position = headerY + 100;

  pdf.addImage(imgData, "PNG", 40, position, imgWidth, imgHeight);
  heightLeft -= pdf.internal.pageSize.getHeight() - position;

  while (heightLeft > 0) {
    pdf.addPage();
    position = 40;
    pdf.addImage(imgData, "PNG", 40, position - heightLeft, imgWidth, imgHeight);
    heightLeft -= pdf.internal.pageSize.getHeight() - 40;
  }

  // --- Footer with pagination ---
  const pageCount = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(9);
    pdf.setTextColor(130);
    pdf.text(
      `Page ${i} of ${pageCount}`,
      pageWidth - 80,
      pdf.internal.pageSize.getHeight() - 30
    );
  }

  pdf.save(`Convalesce_Payslips_${weekDisplay}.pdf`);
};


  // ──────────────────────────────────────────────
  // Cards (added: Approvals List, Generate Payslips, Export buttons)
  // ──────────────────────────────────────────────
  const cards = [
    {
      title: "Generate Timesheets",
      desc: "Create automatic weekly timesheets.",
      bg: "bg-white",
      text: "text-gray-700",
      onClick: generateTimesheets,
      icon: "📆",
    },
	
	
	{
      title: "Manual Timesheet Creator",
      desc: "Create or edit a custom timesheet.",
      bg: "bg-green-500",
      text: "text-white",
      onClick: () => navigate("/admin/manual-timesheet"),
      icon: "📝",
    },
	
	
	
	
	
	
    {
      title: "Approve Timesheets",
      desc: "Review & approve entries below.",
      bg: "bg-blue-500",
      text: "text-white",
      onClick: () => {
        const el = document.getElementById("timesheet-list");
        if (el) el.scrollIntoView({ behavior: "smooth" });
      },
      icon: "✅",
    },
	
	
	{
      title: "View All Payslips",
      desc: "Access and download staff payslips.",
      color: "#ec4899",
      onClick: viewPayslips,
      icon: "📄",
    },
	
	
    {
      title: "Generate Payslips",
      desc: "From approved timesheets (PAYE).",
      bg: "bg-yellow-500",
      text: "text-gray-900",
      onClick: generatePayslipsFromApproved,
      icon: "💰",
    },
    {
      title: "Export PDF",
      desc: "Export payslips / timesheets to PDF.",
      bg: "bg-pink-500",
      text: "text-white",
      onClick: () => {
        // open small chooser
        const choice = window.prompt("Type 'payslips' or 'timesheets' to export:");
        if (choice === "payslips") exportPayslipsPDF();
        else if (choice === "timesheets") exportTimesheetsPDF();
        else alert("Try again with 'payslips' or 'timesheets'.");
      },
      icon: "📄",
    },
  ];

  return (
    <div style={{ paddingTop: "90px" }} className="p-6 pt-20 min-h-screen bg-gray-700/80 text-gray-100">
      <h1
		  style={{
			textAlign: "center",
			color: "gray",
			boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
			width: "fit-content",
			margin: "0 auto",
			fontSize: "16px",
			fontWeight: "500",
			 padding: "14px 28px",
			 borderRadius: "20px",

		  }}
		>
		  Timesheet & Payslip Manager
		</h1>


      {/* Cards */}
      <div style={{ paddingTop: "40px" }}  className="flex justify-center w-full mt-8">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 w-full max-w-md">
          {cards.map((card, idx) => (
            <div
              key={idx}
              onClick={!loading ? card.onClick : null}
              className={`${card.bg} ${card.text} cursor-pointer rounded-2xl p-6 text-center shadow-md hover:scale-105 transition transform ${
                loading ? "opacity-50 pointer-events-none" : ""
              }`}
              style={{ boxShadow: "0 2px 5px #10b981", color:"gray", }}
            >
              <div className="text-4xl mb-2">{card.icon}</div>
              <h3 className="text-lg font-semibold mb-1">{card.title}</h3>
              <p className="text-sm opacity-90">{card.desc}</p>
            </div>
          ))}
        </div>
      </div>

     {/* Week selector */}
		<div
		  style={{
			maxWidth: "900px",
			margin: "40px auto 0 auto",
			backgroundColor: "white",
			borderRadius: "16px",
			padding: "20px 24px",
			boxShadow: "0 4px 8px rgba(0,0,0,0.05)",
			color: "#1f2937",
		  }}
		>
		  <label
			htmlFor="weekSelect"
			style={{
			  display: "block",
			  fontSize: "14px",
			  color: "#6b7280",
			  marginBottom: "8px",
			  fontWeight: "500",
			}}
		  >
			Select Week
		  </label>

		  <select
			id="weekSelect"
			value={selectedWeek}
			onChange={(e) => setSelectedWeek(e.target.value)}
			style={{
			  width: "100%",
			  maxWidth: "300px",
			  padding: "10px 12px",
			  borderRadius: "10px",
			  border: "1px solid #d1d5db",
			  fontSize: "14px",
			  color: "#1f2937",
			  backgroundColor: "#f9fafb",
			  outline: "none",
			  transition: "all 0.2s ease",
			}}
			onFocus={(e) => (e.target.style.border = "1px solid #10b981")}
			onBlur={(e) => (e.target.style.border = "1px solid #d1d5db")}
		  >
			<option value="latest">Latest</option>
			{weeks.map((w) => (
			  <option key={w} value={w}>
				{w}
			  </option>
			))}
		  </select>
		</div>


      {/* Timesheets list */}
	  
			<div
			  id="timesheet-list"
			  style={{
				maxWidth: "900px",
				margin: "40px auto",
				backgroundColor: "white",
				borderRadius: "16px",
				padding: "24px",
				color: "#1f2937",
			  }}
			>
			  <div
				style={{
				  display: "flex",
				  justifyContent: "space-between",
				  alignItems: "center",
				  marginBottom: "16px",
				}}
			  >
				<h2 style={{ fontSize: "20px", fontWeight: "600" }}>
				  Timesheets — Week {selectedWeek === "latest" ? weeks[0] : selectedWeek}
				</h2>
				<button
				  onClick={exportTimesheetsPDF}
				  style={{
					backgroundColor: "#374151",
					color: "white",
					padding: "10px 16px",
					borderRadius: "8px",
					cursor: "pointer",
					fontWeight: "500",
				  }}
				>
				  📄 Export All Timesheets PDF
				</button>
			  </div>

			  {visibleTimesheets.length === 0 && (
				<p style={{ color: "gray" }}>No timesheets for this week.</p>
			  )}

			  {visibleTimesheets.map((ts) => (
			  
			  
			   <div
			  key={ts.id}
			  style={{
				border: "1px solid #e5e7eb",
				borderRadius: "16px",
				padding: "24px",
				marginBottom: "24px",
				backgroundColor: "#f9fafb",
				boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
			  }}
			>
			  {/* Visible Header */}
			  <div
				style={{
				  display: "flex",
				  justifyContent: "space-between",
				  alignItems: "flex-start",
				  borderBottom: "1px solid #e5e7eb",
				  paddingBottom: "8px",
				  marginBottom: "12px",
				}}
			  >
				<div>
				  <h3 style={{ fontSize: "18px", fontWeight: "600" }}>{ts.staffName}</h3>
				  <p style={{ fontSize: "13px", color: "#6b7280", marginTop: "2px" }}>
					{ts.weekStart} → {ts.weekEnd} • Status:{" "}
					<span
					  style={{
						color:
						  ts.status === "Approved"
							? "#16a34a"
							: ts.status === "Submitted"
							? "#f59e0b"
							: "#6b7280",
						fontWeight: "500",
					  }}
					>
					  {ts.status || "Pending"}
					</span>
				  </p>
				</div>

				{/* Action Buttons (not exported) */}
				<div style={{ display: "flex", gap: "8px" }}>
				  <button
					onClick={() => approveAllEntries(ts.id, true)}
					style={{
					  backgroundColor: "#16a34a",
					  color: "white",
					  padding: "6px 10px",
					  borderRadius: "8px",
					  fontSize: "13px",
					  cursor: "pointer",
					}}
				  >
					Approve All
				  </button>
				  <button
					onClick={() => approveAllEntries(ts.id, false)}
					style={{
					  backgroundColor: "#e5e7eb",
					  color: "#111827",
					  padding: "6px 10px",
					  borderRadius: "8px",
					  fontSize: "13px",
					  cursor: "pointer",
					}}
				  >
					Clear
				  </button>
				</div>
			  </div>

			  {/* Visible table */}
			  <div style={{ overflowX: "auto" }}>
				<table
				  style={{
					width: "100%",
					fontSize: "13px",
					borderCollapse: "collapse",
					marginBottom: "10px",
				  }}
				>
				  <thead>
					<tr
					  style={{
						backgroundColor: "#e5e7eb",
						textAlign: "left",
						color: "#374151",
					  }}
					>
					  <th style={{ padding: "8px" }}>Date</th>
					  <th style={{ padding: "8px" }}>Client</th>
					  <th style={{ padding: "8px", textAlign: "right" }}>Hours</th>
					  <th style={{ padding: "8px", textAlign: "right" }}>Rate</th>
					  <th style={{ padding: "8px", textAlign: "right" }}>Amount</th>
					  <th style={{ padding: "8px", textAlign: "center" }}>✔</th>
					</tr>
				  </thead>
				  <tbody>
					{ts.entries?.map((e, idx) => (
					  <tr
						key={idx}
						style={{
						  borderBottom: "1px solid #e5e7eb",
						  backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f3f4f6",
						}}
					  >
						<td style={{ padding: "8px" }}>{e.date}</td>
						<td style={{ padding: "8px" }}>{e.clientName}</td>
						<td style={{ padding: "8px", textAlign: "right" }}>{e.hours}</td>
						<td style={{ padding: "8px", textAlign: "right" }}>
						  {fmtGBP(e.rate)}
						</td>
						<td style={{ padding: "8px", textAlign: "right" }}>
						  {fmtGBP(e.hours * e.rate)}
						</td>
						<td style={{ padding: "8px", textAlign: "center" }}>
						  <input
							type="checkbox"
							checked={!!e.approved}
							onChange={() => toggleEntryApproval(ts.id, idx)}
						  />
						</td>
					  </tr>
					))}
				  </tbody>
				  <tfoot>
					<tr style={{ fontWeight: "600" }}>
					  <td style={{ padding: "8px" }} colSpan={4}>
						Total (All Entries)
					  </td>
					  <td style={{ padding: "8px", textAlign: "right" }}>
						{fmtGBP(ts.totalPay)}
					  </td>
					  <td />
					</tr>
					<tr>
					  <td
						style={{
						  padding: "8px",
						  fontWeight: "600",
						  color: "#16a34a",
						}}
						colSpan={4}
					  >
						Approved Gross
					  </td>
					  <td
						style={{
						  padding: "8px",
						  textAlign: "right",
						  fontWeight: "600",
						  color: "#16a34a",
						}}
					  >
						{fmtGBP(ts.approvedGross || 0)}
					  </td>
					  <td />
					</tr>
				  </tfoot>
				</table>
			  </div>

			  {/* Export-only version (hidden in normal view) */}
			  <div id={`timesheet-${ts.id}`} style={{ display: "none" }}>
				<div
				  style={{
					padding: "24px",
					backgroundColor: "white",
					borderRadius: "12px",
					border: "1px solid #e5e7eb",
				  }}
				>
				  {/* Company header */}
				  <div
					style={{
					  display: "flex",
					  justifyContent: "space-between",
					  alignItems: "center",
					  marginBottom: "16px",
					}}
				  >
					<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
					  <img src={logoImg} alt="CCR" style={{ width: "50px", height: "50px" }} />
					  <div>
						<h2 style={{ fontSize: "18px", fontWeight: "700", color: "#374151" }}>
						  CCR — Convalesce
						</h2>
						<p style={{ fontSize: "12px", color: "#6b7280" }}>
						  Professional and Expert Touch
						</p>
					  </div>
					</div>
					<div style={{ textAlign: "right", fontSize: "12px", color: "#6b7280" }}>
					  <div>Week {ts.weekStart} → {ts.weekEnd}</div>
					  <div>Employee: {ts.staffName}</div>
					</div>
				  </div>

				  {/* Table replicated */}
				  <table
					style={{
					  width: "100%",
					  fontSize: "13px",
					  borderCollapse: "collapse",
					  marginBottom: "10px",
					}}
				  >
					<thead>
					  <tr style={{ backgroundColor: "#f3f4f6", textAlign: "left" }}>
						<th style={{ padding: "8px" }}>Date</th>
						<th style={{ padding: "8px" }}>Client</th>
						<th style={{ padding: "8px", textAlign: "right" }}>Hours</th>
						<th style={{ padding: "8px", textAlign: "right" }}>Rate</th>
						<th style={{ padding: "8px", textAlign: "right" }}>Amount</th>
					  </tr>
					</thead>
					<tbody>
					  {ts.entries?.map((e, idx) => (
						<tr
						  key={idx}
						  style={{
							borderBottom: "1px solid #e5e7eb",
							backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f9fafb",
						  }}
						>
						  <td style={{ padding: "8px" }}>{e.date}</td>
						  <td style={{ padding: "8px" }}>{e.clientName}</td>
						  <td style={{ padding: "8px", textAlign: "right" }}>{e.hours}</td>
						  <td style={{ padding: "8px", textAlign: "right" }}>
							{fmtGBP(e.rate)}
						  </td>
						  <td style={{ padding: "8px", textAlign: "right" }}>
							{fmtGBP(e.hours * e.rate)}
						  </td>
						</tr>
					  ))}
					</tbody>
					<tfoot>
					  <tr style={{ fontWeight: "600" }}>
						<td style={{ padding: "8px" }} colSpan={4}>
						  Total (All Entries)
						</td>
						<td style={{ padding: "8px", textAlign: "right" }}>
						  {fmtGBP(ts.totalPay)}
						</td>
					  </tr>
					  <tr>
						<td
						  style={{
							padding: "8px",
							fontWeight: "600",
							color: "#16a34a",
						  }}
						  colSpan={4}
						>
						  Approved Gross
						</td>
						<td
						  style={{
							padding: "8px",
							textAlign: "right",
							fontWeight: "600",
							color: "#16a34a",
						  }}
						>
						  {fmtGBP(ts.approvedGross || 0)}
						</td>
					  </tr>
					</tfoot>
				  </table>
				</div>
			  </div>

			  {/* Export Button */}
			  <button
				onClick={() =>
				  exportElementToPDF(
					`timesheet-${ts.id}`,
					`Timesheet_${ts.staffName}_${ts.weekStart}.pdf`
				  )
				}
				style={{
				  backgroundColor: "#1e3a8a",
				  color: "white",
				  padding: "10px 16px",
				  borderRadius: "8px",
				  fontSize: "13px",
				  cursor: "pointer",
				}}
			  >
				⬇️ Export This Timesheet
			  </button>
			</div>

				
				
				
			  )
			  
			  
			  )}
			</div>


				  
	  
	  
      
					  
					  {/* Payslips */}
				<div
				  id="payslip-list"
				  style={{
					maxWidth: "900px",
					margin: "40px auto",
					backgroundColor: "white",
					borderRadius: "16px",
					padding: "24px",
					color: "#1f2937",
				  }}
				>
				  <div
					style={{
					  display: "flex",
					  justifyContent: "space-between",
					  alignItems: "center",
					  marginBottom: "16px",
					}}
				  >
					<h2 style={{ fontSize: "20px", fontWeight: "600" }}>
					  Payslips — Week {selectedWeek === "latest" ? weeks[0] : selectedWeek}
					</h2>
					<button
					  onClick={generatePayslipsFromApproved}
					  style={{
						backgroundColor: "#facc15",
						color: "#111827",
						padding: "10px 16px",
						borderRadius: "8px",
						fontWeight: "500",
						cursor: "pointer",
					  }}
					>
					  Generate Payslips (from Approved)
					</button>
				  </div>

				  {payslips.length === 0 && (
					<p style={{ color: "gray", marginTop: "8px" }}>
					  No payslips yet for this week.
					</p>
				  )}

				  <div
					style={{
					  display: "grid",
					  gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
					  gap: "20px",
					  marginTop: "16px",
					}}
				  >
					{payslips.map((p) => (
					  <div
						key={p.id}
						style={{
						  border: "1px solid #e5e7eb",
						  borderRadius: "16px",
						  padding: "24px",
						  backgroundColor: "white",
						  boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
						}}
					  >
						{/* Header */}
						<div
						  style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "flex-start",
							borderBottom: "1px solid #e5e7eb",
							paddingBottom: "12px",
							marginBottom: "12px",
						  }}
						>
						  <div>
							<h3
							  style={{ fontSize: "18px", fontWeight: "600", color: "#1f2937" }}
							>
							  {p.staffName}
							</h3>
							<p
							  style={{
								fontSize: "13px",
								color: "#6b7280",
								marginTop: "4px",
							  }}
							>
							  Week {p.weekStart} → {p.weekEnd} • Weekly • Code {p.taxCode}
							</p>
						  </div>
						  <div style={{ textAlign: "right" }}>
							<p
							  style={{
								fontSize: "12px",
								color: "#6b7280",
								textTransform: "uppercase",
							  }}
							>
							  Net Pay
							</p>
							<p
							  style={{
								fontSize: "20px",
								fontWeight: "700",
								color: "#16a34a",
								marginTop: "2px",
							  }}
							>
							  {fmtGBP(p.netPay)}
							</p>
						  </div>
						</div>

						{/* Summary Stats */}
						<div
						  style={{
							display: "grid",
							gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
							gap: "10px",
							marginBottom: "16px",
						  }}
						>
						  <div
							style={{
							  backgroundColor: "#f9fafb",
							  padding: "10px",
							  borderRadius: "8px",
							  textAlign: "center",
							}}
						  >
							<p
							  style={{
								color: "#6b7280",
								fontSize: "12px",
								textTransform: "uppercase",
							  }}
							>
							  Gross Pay
							</p>
							<p style={{ fontWeight: "600" }}>{fmtGBP(p.grossPay)}</p>
						  </div>
						  <div
							style={{
							  backgroundColor: "#f9fafb",
							  padding: "10px",
							  borderRadius: "8px",
							  textAlign: "center",
							}}
						  >
							<p
							  style={{
								color: "#6b7280",
								fontSize: "12px",
								textTransform: "uppercase",
							  }}
							>
							  Income Tax
							</p>
							<p style={{ fontWeight: "600" }}>{fmtGBP(p.incomeTax)}</p>
						  </div>
						  <div
							style={{
							  backgroundColor: "#f9fafb",
							  padding: "10px",
							  borderRadius: "8px",
							  textAlign: "center",
							}}
						  >
							<p
							  style={{
								color: "#6b7280",
								fontSize: "12px",
								textTransform: "uppercase",
							  }}
							>
							  Employee NI
							</p>
							<p style={{ fontWeight: "600" }}>{fmtGBP(p.employeeNI)}</p>
						  </div>
						  {p.studentLoan > 0 && (
							<div
							  style={{
								backgroundColor: "#f9fafb",
								padding: "10px",
								borderRadius: "8px",
								textAlign: "center",
							  }}
							>
							  <p
								style={{
								  color: "#6b7280",
								  fontSize: "12px",
								  textTransform: "uppercase",
								}}
							  >
								Student Loan
							  </p>
							  <p style={{ fontWeight: "600" }}>{fmtGBP(p.studentLoan)}</p>
							</div>
						  )}
						  {p.pensionEmployee > 0 && (
							<div
							  style={{
								backgroundColor: "#f9fafb",
								padding: "10px",
								borderRadius: "8px",
								textAlign: "center",
							  }}
							>
							  <p
								style={{
								  color: "#6b7280",
								  fontSize: "12px",
								  textTransform: "uppercase",
								}}
							  >
								Pension
							  </p>
							  <p style={{ fontWeight: "600" }}>
								{fmtGBP(p.pensionEmployee)}
							  </p>
							</div>
						  )}
						</div>

						{/* Detailed Table Section */}
						<div
						  id={`payslip-${p.id}`}
						  style={{
							backgroundColor: "#f9fafb",
							border: "1px solid #e5e7eb",
							borderRadius: "8px",
							padding: "16px",
						  }}
						>
						  <div
							style={{
							  display: "flex",
							  justifyContent: "space-between",
							  marginBottom: "8px",
							}}
						  >
							<div>
							  <p style={{ fontWeight: "600", color: "#374151" }}>
								CCR — Convalesce
							  </p>
							  <p style={{ fontSize: "12px", color: "#6b7280" }}>
								Weekly Payslip
							  </p>
							</div>
							<p style={{ fontSize: "12px", color: "#6b7280" }}>
							  {p.weekStart} → {p.weekEnd}
							</p>
						  </div>

						  <div
							style={{
							  fontSize: "13px",
							  color: "#4b5563",
							  marginBottom: "10px",
							}}
						  >
							<p>
							  <span style={{ fontWeight: "500" }}>Employee:</span> {p.staffName}
							</p>
							<p>
							  <span style={{ fontWeight: "500" }}>Tax Code:</span> {p.taxCode}
							</p>
						  </div>

						  <div
							style={{
							  display: "grid",
							  gridTemplateColumns: "1fr 1fr",
							  gap: "20px",
							  fontSize: "13px",
							  color: "#374151",
							}}
						  >
							<div>
							  <h4
								style={{
								  fontWeight: "600",
								  color: "#374151",
								  borderBottom: "1px solid #d1d5db",
								  paddingBottom: "4px",
								  marginBottom: "6px",
								}}
							  >
								Earnings
							  </h4>
							  <div
								style={{
								  display: "flex",
								  justifyContent: "space-between",
								}}
							  >
								<span>Gross Pay</span>
								<span>{fmtGBP(p.grossPay)}</span>
							  </div>
							</div>

							<div>
							  <h4
								style={{
								  fontWeight: "600",
								  color: "#374151",
								  borderBottom: "1px solid #d1d5db",
								  paddingBottom: "4px",
								  marginBottom: "6px",
								}}
							  >
								Deductions
							  </h4>
							  <div
								style={{
								  display: "flex",
								  justifyContent: "space-between",
								}}
							  >
								<span>Income Tax (PAYE)</span>
								<span>{fmtGBP(p.incomeTax)}</span>
							  </div>
							  <div
								style={{
								  display: "flex",
								  justifyContent: "space-between",
								}}
							  >
								<span>Employee NI</span>
								<span>{fmtGBP(p.employeeNI)}</span>
							  </div>
							</div>
						  </div>

						  <div
							style={{
							  borderTop: "1px solid #d1d5db",
							  marginTop: "10px",
							  paddingTop: "8px",
							  textAlign: "right",
							  fontWeight: "600",
							  color: "#16a34a",
							}}
						  >
							Net Pay: {fmtGBP(p.netPay)}
						  </div>
						</div>

						{/* Export Single Payslip */}
						<button
						  onClick={() =>
							exportElementToPDF(
							  `payslip-${p.id}`,
							  `payslip_${p.staffName}_${p.weekStart}.pdf`
							)
						  }
						  style={{
							backgroundColor: "#374151",
							color: "white",
							padding: "10px 16px",
							borderRadius: "8px",
							fontSize: "13px",
							marginTop: "12px",
							cursor: "pointer",
						  }}
						>
						  ⬇️ Export this Payslip
						</button>
					  </div>
					))}
				  </div>

				  <div style={{ marginTop: "24px", textAlign: "center" }}>
					<button
					  onClick={exportPayslipsPDF}
					  style={{
						backgroundColor: "#374151",
						color: "white",
						padding: "12px 18px",
						borderRadius: "8px",
						cursor: "pointer",
						fontWeight: "500",
					  }}
					>
					  📄 Export All Payslips PDF
					</button>
				  </div>
				</div>
	  
	  
	  
	  
	  
	  
	  
	  
    </div>
	
	
	
	
	
  );
}
